import { useAutocompleteSuggestionsLazyQuery } from '@kijiji/generated/graphql-types'
import { useState } from 'react'

import {
  getInternalCategorySuggestions,
  separateInternalSuggestionsByType,
} from '@/components/shared/global-header/search-bar/suggestions/utils/autocompleteHelper'
import { DEBOUNCE_MS } from '@/constants/others'
import {
  getBrandedSuggestion,
  getCategorySuggestions,
  separateSearchSuggestionByType,
} from '@/domain/headerSearchBar'
import { useBrandedSearchCampaign } from '@/hooks/campaigns/useBrandedSearchCampaign'
import { useDebounce } from '@/hooks/useDebounce'
import { useLocale } from '@/hooks/useLocale'
import { RemoteToggles } from '@/lib/firebase/config/toggles'
import { useToggle } from '@/lib/firebase/hooks'
import { type SearchBarOption } from '@/ui/molecules/search-bar'
import { useAlgoliaSearch } from '@/utils/algolia/algoliaSearch'

export const useSearchSuggestions = ({ categoryId }: { categoryId: number }) => {
  const { apiLocale } = useLocale()

  const isAutocompleteSearchEnabled =
    useToggle(RemoteToggles.SEARCH_AUTOCOMPLETE_TOGGLE.key)?.enabled ?? false

  /** Initialize algolia */
  const { getSearchSuggestions } = useAlgoliaSearch()

  const [getAutosuggest] = useAutocompleteSuggestionsLazyQuery()

  /** Get branded-search custom campaigns */
  const { campaigns } = useBrandedSearchCampaign({ categoryId })

  const [suggestionOptions, setSuggestionOptions] = useState<SearchBarOption[]>([])

  const debounce = useDebounce(DEBOUNCE_MS)

  const getSuggestions = async (query: string) => {
    //If there is no search text, then suggestions should be empty
    if (!query) {
      setSuggestionOptions([])
      return
    }

    const brandedSuggestionByKeyword = getBrandedSuggestion(campaigns, query)

    if (isAutocompleteSearchEnabled) {
      // Behind FF toggle_search_autocomplete_query: GraphQL related implementation

      debounce(async () => {
        const res = await getAutosuggest({
          variables: { input: { query, limit: 5 } },
        })
        const graphqlSuggestions = res?.data?.autocompleteSuggestions ?? []

        const { generalResults } = separateInternalSuggestionsByType(graphqlSuggestions)
        const categoryOptions = getInternalCategorySuggestions(generalResults, apiLocale)
        const combinedSuggestions = [...categoryOptions, ...brandedSuggestionByKeyword]

        setSuggestionOptions(combinedSuggestions)
      })()
    } else {
      /**
       * TODO: Figure out what we are going to do with this autos feature after the release
       * We might want to remove it moving forward. For now, we are just not adding to the list of suggestions
       *
       * This comment should lead us to the PR that removed the autos functionality in case we want to add it back
       */
      const suggestions = await getSearchSuggestions(query)

      const { generalResults } = separateSearchSuggestionByType(suggestions)
      const categoryOptions = getCategorySuggestions(generalResults, apiLocale)
      const combinedSuggestions = [...categoryOptions, ...brandedSuggestionByKeyword]

      setSuggestionOptions(combinedSuggestions)
    }
  }

  return { getSuggestions, suggestions: suggestionOptions }
}
